import { AZModal, setFeedBackOpen } from '@azmed-org/azcomponents';
import { MdArrowBack, MdCloudUpload, MdHelpOutline, MdSettings } from 'react-icons/md';
import { asyncComponent, retryImport } from '@ohif/ui';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VscFeedback } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { UserPreferences } from '../../components/UserPreferences';
import { FeedbackWrapper } from '../AZFeedback/FeedbackWrapper';
import { AuthButton } from './AuthButton';
import { Title } from '../Components/Title/Title';
import { selectVertical } from '../Components/Filter/FiltersSlice.js';
import { setKeepFiltersOnReset, triggerResetApp } from '../NavigationSlice.js';
//CSS
import './AZmedNavbar.css';

// #region CONSTANTS
const HELP_LINK =
  'https://www.notion.so/azmed/How-to-use-AZViewer-50f54c9e32154276a7f1c6902d551be2?pvs=4';
// #endregion

// #region STYLES
// AZModal already provides the panel background; these inner containers must
// not paint their own, otherwise the modal shows a nested darker box.
const MODAL_CONTENT = {
  width: '100%',
  height: '100%',
};

const PREFERENCES_CONTENT = {
  width: '100%',
  height: '100%',
  color: 'white',
};
// #endregion

const ViewerLocalFileData = asyncComponent(() =>
  retryImport(() =>
    import(
      /* webpackChunkName: "ViewerLocalFileData" */ '../../connectedComponents/LoadLocalFiles.js'
    )
  )
);

//#region COMPONENT
/**
 * Return the navbar of the App
 * @param {object} props Component properties
 * @returns
 */
function AzmedNavbar() {
  // #region INITIALIZATION
  // State
  const [openDicomModale, setOpenDicomModale] = useState(false);

  // Selectors
  const vertical = useSelector(selectVertical);

  // Hooks
  const history = useHistory();
  const location = useLocation();
  const [t] = useTranslation('Navbar');

  // Dispatch
  const dispatch = useDispatch();

  // References
  // Modal content
  const content = useRef(null);
  // #endregion

  // #region EVENT
  // Close Open dicom modale
  const handleClose = () => {
    content.current = null;
    setOpenDicomModale(false);
  };

  // Return to the study list
  const handleGoBack = () => {
    history.replace('/');
  };

  const handleOpenFeedback = () => {
    dispatch(setFeedBackOpen('manually'));
  };

  const handleClick = () => {
    // reset filters and go to home
    dispatch(triggerResetApp());
    history.replace('/');
  };

  const handleResetAppAndKeepFilters = () => {
    // reset app but keep filters and go to home
    dispatch(setKeepFiltersOnReset(true));
  };
  // #endregion

  const doShowToolbar = () => {
    return (
      location.pathname === '/' ||
      location.pathname.startsWith('/viewer') ||
      location.pathname.startsWith('/local')
    );
  };

  //#region INTERFACE
  return (
    <>
      <div className="AZmedNavbar-root">
        <nav className="AZmedNavbar-appBar">
          <div className="AZmedNavbar-link" onClick={handleClick}>
            <Title
              label="Viewer"
              showImage={true}
              showLines={false}
              capitalize={false}
            />
          </div>
          {doShowToolbar() && (
            <>
              <div className="AZmedNavbar-grow" />
              {location.pathname !== '/' ? (
                <button
                  aria-haspopup="true"
                  className="AZmedNavbar-navButton"
                  onClick={handleGoBack}
                >
                  <MdArrowBack className="AZmedNavbar-Icon" />
                  <div className="AZmedNavbar-sectionDesktop">
                    {t('Study list')}
                  </div>
                </button>
              ) : (
                vertical && (
                  <button
                    aria-haspopup="true"
                    className="AZmedNavbar-navButton"
                    onClick={handleResetAppAndKeepFilters}
                  >
                    <MdArrowBack className="AZmedNavbar-Icon" />
                    <div className="AZmedNavbar-sectionDesktop">
                      {t('Home page')}
                    </div>
                  </button>
                )
              )}
              <button
                aria-haspopup="true"
                className="AZmedNavbar-navButton"
                onClick={() => setOpenDicomModale(true)}
              >
                <MdCloudUpload className="AZmedNavbar-Icon" />
                <div className="AZmedNavbar-sectionDesktop">
                  {t('import local file')}
                </div>
              </button>
              <button
                aria-haspopup="true"
                className="AZmedNavbar-navButton"
                onClick={() => {
                  setOpenDicomModale(true);
                  content.current = () => (
                    <UserPreferences hide={handleClose} />
                  );
                }}
              >
                <MdSettings className="AZmedNavbar-Icon" />
                <div className="AZmedNavbar-sectionDesktop">
                  {t('Settings')}
                </div>
              </button>
              <button
                aria-haspopup="true"
                className="AZmedNavbar-navButton"
                onClick={() => window.open(HELP_LINK, '_blank')}
              >
                <MdHelpOutline className="AZmedNavbar-Icon" />
                <div className="AZmedNavbar-sectionDesktop">{t('Help')}</div>
              </button>
              {/* Feedbacks */}
              <button
                aria-haspopup="true"
                className="AZmedNavbar-navButton"
                onClick={() => handleOpenFeedback()}
              >
                <VscFeedback
                  className="AZmedNavbar-Icon"
                  fontWeight={'bold'}
                />
                <div className="AZmedNavbar-sectionDesktop">{t('Feedback')}</div>
              </button>
              <AuthButton />
            </>
          )}
        </nav>
      </div>
      <AZModal show={openDicomModale} onClose={handleClose} fullscreen={false} maxWidth="900px">
        <div
          className="AZmedNavbar-ModalContainer"
          style={content.current ? PREFERENCES_CONTENT : MODAL_CONTENT}
        >
          {content.current ? (
            content.current()
          ) : (
            <ViewerLocalFileData onLoaded={handleClose} />
          )}
        </div>
      </AZModal>
      <FeedbackWrapper />
    </>
  );
  //#endregion
}
// #endregion

export default AzmedNavbar;
